import React from 'react';
import { FileText, CheckCircle, XCircle, BarChart2 } from 'lucide-react';

interface PriorArtAnalysisDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showHeader?: boolean;
}

const PriorArtAnalysisDisplay: React.FC<PriorArtAnalysisDisplayProps> = ({
  result,
  theme = 'light',
  showHeader = true,
}) => {
  const isDark = theme === 'dark';

  let parsed = result;
  if (typeof result === 'string') {
    try { parsed = JSON.parse(result); } catch { return null; }
  }

  const data = parsed?.output || parsed;

  if (!data || (!data.references_total && data.references_total !== 0)) return null;

  const kept = data.references_kept ?? 0;
  const total = data.references_total ?? 0;
  const dropped = total - kept;
  const features = data.element_map_features ?? 0;
  const reports: Record<string, string> = data.reports ?? {};

  const card = isDark
    ? 'bg-gray-800 border border-gray-700 rounded-lg p-4'
    : 'bg-white border border-gray-200 rounded-lg p-4 shadow-sm';
  const labelCls = isDark ? 'text-gray-400 text-xs' : 'text-gray-500 text-xs';
  const valueCls = isDark ? 'text-white font-semibold text-lg' : 'text-gray-900 font-semibold text-lg';

  return (
    <div className="space-y-4">
      {showHeader && (
        <h3 className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          Prior Art Analysis Results
        </h3>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className={card}>
          <div className={labelCls}>Total References</div>
          <div className={`${valueCls} flex items-center gap-1 mt-1`}>
            <BarChart2 className="w-4 h-4 text-blue-500" />
            {total}
          </div>
        </div>
        <div className={card}>
          <div className={labelCls}>Kept (Score &gt; 0)</div>
          <div className={`${valueCls} flex items-center gap-1 mt-1`}>
            <CheckCircle className="w-4 h-4 text-green-500" />
            {kept}
          </div>
        </div>
        <div className={card}>
          <div className={labelCls}>Dropped</div>
          <div className={`${valueCls} flex items-center gap-1 mt-1`}>
            <XCircle className="w-4 h-4 text-red-400" />
            {dropped}
          </div>
        </div>
      </div>

      {features > 0 && (
        <div className={isDark ? 'text-gray-300 text-sm' : 'text-gray-600 text-sm'}>
          <span className="font-medium">{features}</span> features extracted from technology description
        </div>
      )}

      {/* Report files */}
      {Object.keys(reports).length > 0 && (
        <div>
          <div className={`font-medium text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Generated Reports
          </div>
          <div className="space-y-2">
            {Object.entries(reports).map(([name, path]) => (
              <div
                key={name}
                className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
              >
                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="font-medium">{name}</span>
                <span className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {path.split(/[\\/]/).pop()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PriorArtAnalysisDisplay;
